/** Bulk Set blackout on outline Service Lines — dialog once, append the range to each line, patch each line. */
import { ServiceLine } from '@jms/model';
import { firstValueFrom } from 'rxjs';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BulkCommandArgs, BulkCommandOutcome } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';
import { isOutlineServiceLine } from '../outline.selection';
import { BlackoutOwnerRanges, mergeBlackoutRange, OutlineBlackoutRange, OutlineSetBlackoutDialogComponent } from '../dialogs/outline.set.blackout';

export const OUTLINE_SET_BLACKOUT_ACTION = SemanticCommand.SetBlackout;

/** The one label every surface uses — ⌘K, the Action Bar overflow, and the outline bulk bar. */
export const OUTLINE_BLACKOUT_LABEL = 'Set blackout…';

/** `calendar-xmark` ships solid only in the FA6 pack, so the regular variant would render nothing. */
export const OUTLINE_BLACKOUT_ICON = 'fa-solid fa-calendar-xmark';

/** A line blackout stops that line only; sibling lines on the same plan keep their own cadence. */
export const OUTLINE_BLACKOUT_CAPTION = 'Pauses only this line\'s tasks — other lines keep running';

export interface OutlineSetBlackoutDeps {
  /** The entity pair this run is acting on, so one handler serves the live outline and the template one. */
  kinds: OutlineEntityKinds;
  dialogs: WorkBuddyDialogService;
  entityData: EntityDataService;
  entityDataRaw: EntityDataServiceRaw;
  toasts: ToastService;
  /** Overridable so a Detail run can hand over the record it already holds instead of re-reading it. */
  loadLine?: (id: string) => Promise<BlackoutOwnerRanges | null>;
}

/**
 * The outline row carries no `blackoutRanges` — the projection has no column for them — so every line
 * is read back before it is written. The API replaces the array wholesale, so a patch that sent only
 * the new range would drop every blackout already on the line.
 */
export async function handleOutlineSetBlackout(args: BulkCommandArgs<OutlineRow>, deps: OutlineSetBlackoutDeps): Promise<BulkCommandOutcome> {

  if ((args.bulkActionRequest?.actionId ?? args.action) !== OUTLINE_SET_BLACKOUT_ACTION) return { handled: false };
  const lines = (args.data ?? []).filter(row => isOutlineServiceLine(row, deps.kinds));
  if (!lines.length) return { handled: true, clearSelection: false };

  // Wide enough for two month grids side by side — the picker is the dialog, not a field inside one.
  const range = await deps.dialogs.modal<OutlineBlackoutRange, OutlineSetBlackoutDialogComponent>(OutlineSetBlackoutDialogComponent, {
    width: 'min(640px, calc(100vw - 40px))',
    inputs: { caption: OUTLINE_BLACKOUT_CAPTION },
    aria: { label: 'Set blackout' },
  });
  if (!range) return { handled: true, clearSelection: false };

  const loadLine = deps.loadLine ?? (async (id: string) => (await firstValueFrom(deps.entityDataRaw.getById<ServiceLine>(deps.kinds.line, id)))?.item ?? null);
  const patcher = deps.entityData.getPatch(deps.kinds.line);
  let updated = 0;
  let failed = 0;
  for (const line of lines) {
    try {
      const record = await loadLine(line.sourceId);
      await patcher.mutateAsync({ id: line.sourceId, data: { blackoutRanges: mergeBlackoutRange(record?.blackoutRanges, range) } });
      updated += 1;
    } catch {
      failed += 1;
    }
  }

  if (failed && !updated) deps.toasts.error('Set blackout', 'Action failed, please try again.');
  else if (failed) deps.toasts.warning('Set blackout', `Updated ${updated} of ${lines.length} lines.`);
  else deps.toasts.success('Set blackout', `Updated ${updated} line${updated === 1 ? '' : 's'}.`);
  return { handled: true };
}
